/**
 * Write or edit an announcement.
 *
 * Saving never publishes. A new announcement lands as a draft, and an
 * edit leaves the status exactly where it was — so "I fixed a typo" can
 * never turn into "I sent it to twenty people". Publishing is its own
 * button on the list.
 *
 * The preview is the point of the screen. This is the only place an
 * admin gets to see the thing the way a rep will, before anyone else
 * does — which is precisely what was missing when announcements shipped
 * with a deploy.
 */

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useMediaQuery, useTheme } from '@mui/material';
import { MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Alert, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
  type AdminAnnouncement,
  type AnnouncementMark,
  type AnnouncementWrite,
  type Presentation,
} from '../../api/announcements';
import { AnnouncementBanner } from '../../components/announcements/AnnouncementBanner';
import { AnnouncementPreview } from './AnnouncementPreview';
import { colors, docLeading, uiType } from '../../theme';

const GOLD = colors.goldDark;

/** Where a call to action is allowed to point. */
const DESTINATIONS = [
  { label: 'Regional Sales', to: '/regional-sales' },
  { label: 'Depletions', to: '/sales/depletions' },
  { label: 'User Guide', to: '/user-guide' },
  { label: "What's New", to: '/whats-new' },
  { label: 'Home', to: '/' },
];

const BODY_MAX = 600;
const TITLE_MAX = 120;

const EMPTY: AnnouncementWrite = {
  title: '',
  body: '',
  action_label: null,
  action_to: null,
  presentation: 'banner',
  mark: 'hy',
  published_on: null,
  expires_on: null,
};

function toForm(a: AdminAnnouncement): AnnouncementWrite {
  return {
    title: a.title,
    body: a.body,
    action_label: a.action_label,
    action_to: a.action_to,
    presentation: a.presentation,
    mark: a.mark ?? 'hy',
    published_on: a.published_on,
    expires_on: a.expires_on,
  };
}

export function AnnouncementDialog({
  open,
  editing,
  onClose,
}: {
  open: boolean;
  /** null = writing a new one. */
  editing: AdminAnnouncement | null;
  onClose: () => void;
}) {
  const theme = useTheme();
  // A form this long inside a floating card on a phone leaves a strip of
  // dimmed page down each side and a viewport too short to type in.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<AnnouncementWrite>(EMPTY);
  const [fullPreview, setFullPreview] = useState(false);
  const create = useCreateAnnouncement();
  const update = useUpdateAnnouncement();
  const saving = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  // Reset whenever the dialog opens so a cancelled edit never leaks into
  // the next one.
  useEffect(() => {
    if (open) setForm(editing ? toForm(editing) : EMPTY);
  }, [open, editing]);

  const set = <K extends keyof AnnouncementWrite>(key: K, value: AnnouncementWrite[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  /** A destination and its label move together — see the DB constraint. */
  const setDestination = (to: string) => {
    if (!to) {
      setForm((f) => ({ ...f, action_to: null, action_label: null }));
      return;
    }
    const match = DESTINATIONS.find((d) => d.to === to);
    setForm((f) => ({
      ...f,
      action_to: to,
      action_label: f.action_label || `Open ${match?.label ?? 'it'}`,
    }));
  };

  const valid = form.title.trim().length > 0 && form.body.trim().length > 0;

  const save = () => {
    const body: AnnouncementWrite = {
      ...form,
      title: form.title.trim(),
      body: form.body.trim(),
      action_label: form.action_to ? (form.action_label?.trim() ?? null) : null,
      action_to: form.action_to || null,
    };
    const done = { onSuccess: onClose };
    if (editing) update.mutate({ id: editing.id, body }, done);
    else create.mutate(body, done);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {editing ? 'Edit Announcement' : 'New Announcement'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error.message}</Alert>}

          <TextField
            label="Title"
            value={form.title}
            onChange={(e) => set('title', e.target.value.slice(0, TITLE_MAX))}
            helperText={`A few words. ${TITLE_MAX - form.title.length} characters left.`}
            fullWidth
            autoFocus
          />

          <TextField
            label="Body"
            value={form.body}
            onChange={(e) => set('body', e.target.value.slice(0, BODY_MAX))}
            helperText={`One or two sentences — what changed and why they should care. ${
              BODY_MAX - form.body.length
            } characters left.`}
            multiline
            minRows={3}
            fullWidth
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              select
              label="Button Goes To"
              value={form.action_to ?? ''}
              onChange={(e) => setDestination(e.target.value)}
              sx={{ flex: 1 }}
              helperText="Optional. In-app pages only."
            >
              <MenuItem value="">No Button</MenuItem>
              {DESTINATIONS.map((d) => (
                <MenuItem key={d.to} value={d.to}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Button Text"
              value={form.action_label ?? ''}
              onChange={(e) => set('action_label', e.target.value)}
              disabled={!form.action_to}
              sx={{ flex: 1 }}
              helperText={form.action_to ? ' ' : 'Pick a destination first.'}
            />
          </Stack>

          <Box>
            <Typography
              sx={{
                fontSize: uiType.caption,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: colors.textMuted,
                mb: 0.75,
              }}
            >
              How It Appears
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.presentation}
              onChange={(_, v: Presentation | null) => v && set('presentation', v)}
            >
              <ToggleButton value="banner" sx={{ textTransform: 'none', px: 2 }}>
                Banner
              </ToggleButton>
              <ToggleButton value="modal" sx={{ textTransform: 'none', px: 2 }}>
                Popup
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography
              sx={{
                fontSize: uiType.caption,
                color: colors.textMuted,
                mt: 0.75,
                lineHeight: docLeading.normal,
              }}
            >
              {form.presentation === 'banner'
                ? 'Sits under the header. Quiet — people see it without being stopped.'
                : 'Stops the page until it is closed. Save it for things that would cost someone their day to miss — a popup on every release teaches people to close them unread.'}
            </Typography>
          </Box>

          {form.presentation === 'modal' && (
            <Box>
              <Typography
                sx={{
                  fontSize: uiType.caption,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  mb: 0.75,
                }}
              >
                Logo
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={form.mark}
                onChange={(_, v: AnnouncementMark | null) => v && set('mark', v)}
              >
                <ToggleButton value="hy" sx={{ textTransform: 'none', px: 2 }}>
                  Hooten Young
                </ToggleButton>
                <ToggleButton value="ask_abhi" sx={{ textTransform: 'none', px: 2 }}>
                  Ask ABHI
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography
                sx={{
                  fontSize: uiType.caption,
                  color: colors.textMuted,
                  mt: 0.75,
                  lineHeight: docLeading.normal,
                }}
              >
                {form.mark === 'ask_abhi'
                  ? 'Shows the Ask ABHI logo and a small "bottom-right of every page" cue so people can find the assistant.'
                  : 'The Hooten Young monogram — right for most announcements.'}
              </Typography>
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              type="date"
              label="Show From"
              value={form.published_on ?? ''}
              onChange={(e) => set('published_on', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              helperText="Leave blank to use the day you publish."
            />
            <TextField
              type="date"
              label="Stop Showing On"
              value={form.expires_on ?? ''}
              onChange={(e) => set('expires_on', e.target.value || null)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
              helperText="Optional. Retires itself on this date."
            />
          </Stack>

          {/* ---- Preview ----
              Rendered with the real banner component rather than a
              mock-up, so what an admin approves is literally what ships.
              A hand-drawn approximation would be a second thing to keep
              in sync, and the first time it drifted the approval would
              be worthless. */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                sx={{
                  flex: 1,
                  fontSize: uiType.caption,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: GOLD,
                }}
              >
                Preview
              </Typography>
              {valid && (
                <Button
                  size="small"
                  startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setFullPreview(true)}
                  sx={{ textTransform: 'none', fontWeight: 600, color: GOLD }}
                >
                  {form.presentation === 'modal' ? 'Preview Popup' : 'Preview Full Size'}
                </Button>
              )}
            </Box>
            {valid ? (
              <Box
                sx={{
                  border: `1px solid ${colors.borderLight}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <AnnouncementBanner
                  announcement={{
                    key: 'preview',
                    title: form.title,
                    body: form.body,
                    presentation: form.presentation,
                    mark: form.mark,
                    action:
                      form.action_to && form.action_label
                        ? { label: form.action_label, to: form.action_to }
                        : null,
                  }}
                  onDismiss={() => {}}
                />
              </Box>
            ) : (
              <Typography sx={{ fontSize: uiType.body, color: colors.textMuted }}>
                Add a title and body to see how it will look.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <Typography
          sx={{
            fontSize: uiType.caption,
            color: colors.textMuted,
            mr: { sm: 'auto' },
            textAlign: { xs: 'center', sm: 'left' },
          }}
        >
          {editing
            ? 'Anyone who already closed this will not see your edit — write a new one to reach them again.'
            : 'Saves as a draft. Nobody sees it until you publish.'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!valid || saving}
            sx={{
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flex: { xs: 1, sm: 'none' },
              bgcolor: GOLD,
              '&:hover': { bgcolor: colors.gold },
            }}
          >
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Save Draft'}
          </Button>
        </Box>
      </DialogActions>
      <AnnouncementPreview
        announcement={
          fullPreview
            ? {
                key: 'preview',
                title: form.title,
                body: form.body,
                presentation: form.presentation,
                mark: form.mark,
                action:
                  form.action_to && form.action_label
                    ? { label: form.action_label, to: form.action_to }
                    : null,
              }
            : null
        }
        onClose={() => setFullPreview(false)}
      />
    </Dialog>
  );
}
